import { useId, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { CreativeVersionView, PropertyPublic } from "@townsy/contracts";
import { putPlacement } from "../../api/advertising";
import { adKeys, invalidateAdvertising, useMyCreativesQuery, useMyStoresQuery } from "../../api/advertisingQueries";
import { useBootstrapQuery } from "../../api/queries";
import { isApiError } from "../../api/client";
import { useTownSession } from "../../app/TownSessionContext";
import { approvedOfKind, loadErrorMessage, placementErrorMessage } from "../../advertise/advertiserRules";
import { previewOfCreative } from "../../advertise/creativeModel";
import { isSameOriginApiImage } from "../../advertise/media";
import { useTownStore } from "../../state/store";
import { Dialog } from "../Dialog";
import { SignPreview } from "./SignPreview";

/**
 * The page's one "Show on my store" picker (ADR-0009 #8). The current owner applies one of their
 * APPROVED store-sign designs to a store they own; the server checks ownership (a former owner is
 * refused, A25), approval and the store version, bumps the presentation version and tells everyone
 * in town, so the sign changes live. Opened from the store panel (store fixed) or the library
 * (design fixed).
 */
export function PlacementHost() {
  const request = useTownStore((s) => s.placementPicker);
  if (!request) return null;
  return <PlacementDialogContainer key={request.id} propertyId={request.propertyId} creativeId={request.creativeId} />;
}

function PlacementDialogContainer({ propertyId, creativeId }: { propertyId: string | null; creativeId: string | null }) {
  const close = useTownStore((s) => s.closePlacementPicker);
  const pushToast = useTownStore((s) => s.pushToast);
  const session = useTownSession();
  const qc = useQueryClient();
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const storesQ = useMyStoresQuery(signedIn);
  const creativesQ = useMyCreativesQuery(signedIn);
  const [storeId, setStoreId] = useState<string | null>(propertyId);
  const [chosen, setChosen] = useState<string | null>(creativeId);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stores = storesQ.data ?? [];
  const approved = approvedOfKind(creativesQ.data?.items ?? [], "storefront");
  const effectiveStore = storeId ?? (stores.length === 1 ? stores[0]!.id : null);
  const effectiveCreative = chosen ?? (approved.length === 1 ? approved[0]!.creativeId : null);

  const apply = async () => {
    const store = stores.find((s) => s.id === effectiveStore);
    if (!store || !effectiveCreative || pending) return;
    setPending(true);
    setError(null);
    try {
      await putPlacement(store.id, { creativeId: effectiveCreative, expectedPropertyVersion: store.version });
      const c = approved.find((x) => x.creativeId === effectiveCreative);
      close();
      pushToast(`${store.address} now shows “${c?.title ?? "your design"}”. Everyone in town sees the new sign.`, "success");
      invalidateAdvertising(qc, { properties: true, stats: true });
      if (session) void session.refreshProperty(store.id);
    } catch (err) {
      setError(placementErrorMessage(err));
      if (isApiError(err) && (err.code === "PROPERTY_VERSION_CHANGED" || err.code === "FORBIDDEN")) void qc.invalidateQueries({ queryKey: adKeys.myStores });
      if (isApiError(err) && err.code === "CREATIVE_NOT_APPROVED") void qc.invalidateQueries({ queryKey: adKeys.myCreatives });
    } finally {
      setPending(false);
    }
  };

  let unavailable: string | null = null;
  if (boot.isPending) unavailable = "Checking your session…";
  else if (!signedIn) unavailable = "Sign in with the account that owns the store to change its sign.";
  else if (storesQ.isPending || creativesQ.isPending) unavailable = "Loading your stores and designs…";
  else if (storesQ.error) unavailable = loadErrorMessage(storesQ.error, "your stores");
  else if (creativesQ.error) unavailable = loadErrorMessage(creativesQ.error, "your designs");

  return (
    <PlacementPickerView
      stores={stores}
      approved={approved}
      fixedStoreId={propertyId}
      fixedCreativeId={creativeId}
      storeId={effectiveStore}
      creativeId={effectiveCreative}
      unavailable={unavailable}
      pending={pending}
      error={error}
      onSelectStore={(id) => {
        setStoreId(id);
        setError(null);
      }}
      onSelectCreative={(id) => {
        setChosen(id);
        setError(null);
      }}
      onApply={() => void apply()}
      onClose={() => !pending && close()}
      onRetry={() => {
        void storesQ.refetch();
        void creativesQ.refetch();
      }}
    />
  );
}

export interface PlacementPickerViewProps {
  stores: PropertyPublic[];
  approved: CreativeVersionView[];
  fixedStoreId: string | null;
  fixedCreativeId: string | null;
  storeId: string | null;
  creativeId: string | null;
  /** Loading / sign-in / load error text (the choices are not shown). */
  unavailable: string | null;
  pending: boolean;
  error: string | null;
  onSelectStore(id: string): void;
  onSelectCreative(id: string): void;
  onApply(): void;
  onClose(): void;
  onRetry?: () => void;
}

/** Why "Show" is disabled, or null when it can be pressed. */
export function placementBlockedReason(p: Pick<PlacementPickerViewProps, "stores" | "approved" | "fixedStoreId" | "storeId" | "creativeId" | "pending">): string | null {
  if (p.pending) return "Saving…";
  if (p.fixedStoreId && !p.stores.some((s) => s.id === p.fixedStoreId)) {
    return "Only the store's current owner can change its sign. If it was taken over, you can no longer edit it.";
  }
  if (p.stores.length === 0) return "You don't own a store right now. Buy one in town, then show your approved design on it.";
  if (p.approved.length === 0) return "You have no approved store-sign design yet. Create one in the advertiser dashboard; a reviewer approves it first.";
  if (!p.storeId) return "Choose one of your stores.";
  if (!p.creativeId || !p.approved.some((c) => c.creativeId === p.creativeId)) return "Choose an approved design.";
  return null;
}

export function PlacementPickerView(props: PlacementPickerViewProps) {
  const { stores, approved, fixedStoreId, fixedCreativeId, storeId, creativeId, unavailable, pending, error, onSelectStore, onSelectCreative, onApply, onClose, onRetry } = props;
  const formId = useId();
  const store = stores.find((s) => s.id === storeId) ?? null;
  const creative = approved.find((c) => c.creativeId === creativeId) ?? null;
  const blocked = unavailable ? unavailable : placementBlockedReason(props);
  const footer = (
    <>
      {blocked && !pending ? (
        <span className="hint" id={`${formId}-blocked`}>
          {blocked}
        </span>
      ) : null}
      <button type="button" className="btn" onClick={onClose} disabled={pending}>
        Cancel
      </button>
      <button type="button" className="btn btn-primary" disabled={Boolean(blocked)} aria-describedby={blocked ? `${formId}-blocked` : undefined} onClick={onApply} data-testid="placement-apply">
        {pending ? "Saving…" : store && creative ? `Show it on ${store.address}` : "Show on my store"}
      </button>
    </>
  );
  return (
    <Dialog title="Show on my store" onClose={onClose} describedBy={`${formId}-intro`} wide footer={footer}>
      <div className="stack" data-testid="placement-picker">
        <p id={`${formId}-intro`} className="hint" style={{ margin: 0 }}>
          Your store's sign shows an approved design. Everyone in town sees the change right away; your earlier designs stay in your library. Only the
          store's current owner can do this.
        </p>
        {unavailable ? (
          <div className="notice notice-info" role="status">
            {unavailable}
            {onRetry && !/Loading|Checking/.test(unavailable) ? (
              <button type="button" className="btn btn-sm" style={{ marginLeft: 8 }} onClick={onRetry}>
                Try again
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {fixedStoreId ? null : (
              <fieldset className="choice-group" disabled={pending}>
                <legend>Your stores ({stores.length})</legend>
                {stores.length === 0 ? <p className="muted small">You don't own a store right now.</p> : null}
                {stores.map((s) => (
                  <label key={s.id} className="choice placement-store">
                    <input type="radio" name={`${formId}-store`} checked={storeId === s.id} onChange={() => onSelectStore(s.id)} data-testid={`placement-store-${s.id}`} />
                    <span>
                      <strong>{s.address}</strong>
                      <span className="small muted"> · now showing {currentSignLabel(s)}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
            {fixedStoreId && store ? (
              <p className="small" style={{ margin: 0 }}>
                <strong>{store.address}</strong> · now showing {currentSignLabel(store)}
              </p>
            ) : null}
            {fixedCreativeId && creative ? (
              <div className="row" style={{ alignItems: "flex-start" }}>
                <SignPreview model={previewOfCreative(creative, isSameOriginApiImage(creative.imageUrl) ? creative.imageUrl : null)} size="sm" caption={false} />
                <p className="small" style={{ margin: 0 }}>
                  <strong>{creative.title}</strong> · version {creative.version}
                </p>
              </div>
            ) : (
              <fieldset className="choice-group" disabled={pending}>
                <legend>Approved store-sign designs ({approved.length})</legend>
                {approved.length === 0 ? (
                  <p className="small muted" style={{ margin: 0 }}>
                    None yet. <Link to="/advertise?tab=designs" onClick={onClose}>Create a design</Link>; it is shown here once a reviewer approves it.
                  </p>
                ) : null}
                <div className="placement-creatives">
                  {approved.map((c) => (
                    <label key={`${c.creativeId}:${c.version}`} className="choice placement-creative">
                      <input type="radio" name={`${formId}-creative`} checked={creativeId === c.creativeId} onChange={() => onSelectCreative(c.creativeId)} data-testid={`placement-creative-${c.creativeId}`} />
                      <SignPreview model={previewOfCreative(c, isSameOriginApiImage(c.imageUrl) ? c.imageUrl : null)} size="sm" caption={false} />
                      <span className="small">
                        <strong>{c.title}</strong> · v{c.version}
                        {c.destinationHost ? ` · ${c.destinationHost}` : " · no website"}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </>
        )}
        {error ? (
          <div className="notice notice-danger" role="alert" data-testid="placement-error">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/** What a store's sign shows now, in words. */
/** After a purchase the store gets a neutral placeholder with the owner's name and the "NEW" mark (ADR-0007 notes). */
export function isPlaceholderSign(s: Pick<PropertyPublic, "placement">): boolean {
  const p = s.placement;
  return Boolean(p && !p.isDemo && !p.logoTextureUrl && !p.hasWebsite && p.monogram === "NEW");
}

export function currentSignLabel(s: Pick<PropertyPublic, "placement">): string {
  const p = s.placement;
  if (!p) return "no sign";
  if (p.isDemo) return `the demo sign “${p.businessName}”`;
  if (isPlaceholderSign(s)) return `the neutral placeholder with your name (“${p.businessName}”)`;
  return `“${p.businessName}”`;
}
